/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { App } from 'antd'
import { DeleteOutlined, ExportOutlined, PlusOutlined } from '@ant-design/icons'
import { useState } from 'react'
import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { formatShortDate } from '@sportninja/common/ts-utils/date-utils'
import {
  API_ACCESS_SUCCESS_MESSAGES,
  formatApiTokenAccess,
  type AccountApiAccess,
  type ApiToken,
} from '@sportninja/common/features/account/apiAccess'
import WarningModal from 'src/components/WarningModal'
import Button from 'src/components/ts-components/Button'
import DataGrid from 'src/components/ts-components/DataGrid'
import EmptyState from 'src/components/ts-components/EmptyState'
import { formatSuccessToast } from 'src/helpers/formatSuccessToast'
import { API_ACCESS_EMPTY_MIN_HEIGHT } from '../constants'
import { useApiTokenMutations } from '../hooks/useApiTokenMutations'
import { useApiTokens } from '../hooks/useApiTokens'
import { ApiAccessCard } from './ApiAccessCard'
import { ApiAccessSkeleton } from './ApiAccessSkeleton'
import ApiTokenModal from './ApiTokenModal'

interface ApiTokensSectionProps {
  accountUid: string
  access: AccountApiAccess
  /** The token API's docs; `null` hides the link until the API base is known. */
  apiDocsUrl: string | null
}

const formatDate = (value: string | null | undefined, fallback: string) =>
  value ? formatShortDate(value, false) : fallback

/**
 * The signed-in user's API tokens, for server-to-server integrations. A token
 * acts as its creator, so each admin manages their own.
 */
export const ApiTokensSection = ({
  accountUid,
  access,
  apiDocsUrl,
}: ApiTokensSectionProps) => {
  const { message } = App.useApp()
  const { data: apiTokens, isLoading } = useApiTokens(accountUid)
  const { remove } = useApiTokenMutations(accountUid)
  const [isCreating, setIsCreating] = useState(false)
  const [deleting, setDeleting] = useState<ApiToken | null>(null)

  const confirmDelete = async () => {
    if (!deleting) {
      return
    }
    try {
      await remove.mutateAsync(deleting.id)
      message.success(
        formatSuccessToast(API_ACCESS_SUCCESS_MESSAGES.apiTokenDeleted)
      )
      setDeleting(null)
    } catch (err) {
      message.error((err as Error)?.message)
    }
  }

  const columnDefs: ColDef<ApiToken>[] = [
    { headerName: 'Name', field: 'name', flex: 1, minWidth: 200 },
    {
      headerName: 'Access',
      field: 'abilities',
      width: 140,
      valueFormatter: ({ value }) => formatApiTokenAccess(value ?? []),
    },
    {
      headerName: 'Created',
      field: 'created_at',
      width: 160,
      valueFormatter: ({ value }) => formatDate(value, ''),
    },
    {
      headerName: 'Last Used',
      field: 'last_used_at',
      width: 160,
      valueFormatter: ({ value }) => formatDate(value, 'Never'),
    },
    {
      headerName: '',
      colId: 'actions',
      width: 70,
      sortable: false,
      cellRenderer: ActionsCell,
      cellRendererParams: { onDelete: setDeleting },
    },
  ]

  const renderBody = () => {
    if (isLoading && !apiTokens) {
      return <ApiAccessSkeleton />
    }
    if (!apiTokens?.length) {
      return (
        <EmptyState
          title={capitalizeWords('No API Tokens Yet')}
          minHeight={API_ACCESS_EMPTY_MIN_HEIGHT}
          subtitle='Create a token to connect another system to SportNinja.'
        />
      )
    }
    return (
      <DataGrid<ApiToken>
        rowData={apiTokens}
        columnDefs={columnDefs}
        getRowId={({ data }) => String(data.id)}
        domLayout='autoHeight'
        autoHeightMinBodyHeight={0}
      />
    )
  }

  return (
    <ApiAccessCard
      title={capitalizeWords('API Tokens')}
      subtitle='Tokens for connecting other systems. A token acts as you, with your permissions.'
      action={
        <>
          {apiDocsUrl ? (
            <Button
              size='compact'
              variant='outlined'
              icon={<ExportOutlined />}
              text={capitalizeWords('API Docs')}
              onClick={() =>
                window.open(apiDocsUrl, '_blank', 'noopener,noreferrer')
              }
            />
          ) : null}
          <Button
            size='compact'
            variant='primary'
            icon={<PlusOutlined />}
            text={capitalizeWords('Create API Token')}
            onClick={() => setIsCreating(true)}
          />
        </>
      }
    >
      {renderBody()}
      <ApiTokenModal
        isOpen={isCreating}
        onClose={() => setIsCreating(false)}
        accountUid={accountUid}
        access={access}
      />
      <WarningModal
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title={capitalizeWords('Delete API Token')}
        description={`Anything using "${
          deleting?.name ?? ''
        }" will stop working. This cannot be undone.`}
        confirmLabel='DELETE'
        isLoading={remove.isPending}
        destructive
      />
    </ApiAccessCard>
  )
}

interface ActionsCellProps extends ICellRendererParams<ApiToken> {
  onDelete: (apiToken: ApiToken) => void
}

const ActionsCell = ({ data, onDelete }: ActionsCellProps) =>
  data ? (
    <div css={actionsStyle}>
      <Button
        size='compact'
        variant='outlined'
        iconOnly
        icon={<DeleteOutlined />}
        text={capitalizeWords('Delete API Token')}
        onClick={() => onDelete(data)}
      />
    </div>
  ) : null

const actionsStyle = css`
  display: flex;
  align-items: center;
  height: 100%;
`
